import React, { useEffect } from 'react';
import { Card } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import styles from '../styles/Activity.module.scss';
import GridColumnInputWrapper from '@components/common/GridColumnInputWrapper';
import { InputWrapper } from 'react-lib/dist/components-es';
import RiskCheckbox from './RiskCheckbox';
import { MitigationMeasures } from '@components/Activity/types/MitigationMeasures';
import { PreventionMeasures } from '@components/Activity/types/PreventionMeasures';
import { AffectedGroupState } from '@components/Activity/types/AffectedGroupState';
import { FlammableCategory } from '@components/Activity/types/FlammableCategory';
import { OrganisationalMeasures } from '@components/Activity/types/OrganisationalMeasures';
import ExternalDocumentTagsUrlInputWrapper from '@components/common/ExternalDocumentTagsUrlInputWrapper';
import { useFormContext } from 'react-hook-form';
import { edmsReferenceValidation } from '@components/Activity/types/Validations';
import lodash from 'lodash';
import { WorkerTrainingInformation } from '@components/Activity/types/WorkerTrainingInformation';

type ItemKeys =
  | keyof MitigationMeasures
  | keyof PreventionMeasures
  | keyof AffectedGroupState
  | keyof FlammableCategory
  | keyof OrganisationalMeasures
  | keyof WorkerTrainingInformation;

type CommonProps<
  T extends
    | MitigationMeasures
    | PreventionMeasures
    | AffectedGroupState
    | FlammableCategory
    | OrganisationalMeasures
    | WorkerTrainingInformation
> = {
  items: ItemKeys[];
  state: T;
  setState?: (newState: T) => void;
  text?: string;
  setText?: (text: string) => void;
  titleKey: string;
  checkboxKeyPrefix: string;
  otherKey?: keyof T;
  edms?: string;
  setEdms?: (edms: string) => void;
  placeholder?: string;
  placeholderOther?: string;
  disabled?: boolean;
};

const RiskCheckboxCommon = <
  T extends
    | MitigationMeasures
    | PreventionMeasures
    | AffectedGroupState
    | FlammableCategory
    | OrganisationalMeasures
    | WorkerTrainingInformation
>({
  items,
  state,
  setState,
  text,
  setText,
  titleKey,
  checkboxKeyPrefix,
  otherKey,
  edms,
  setEdms,
  placeholder,
  placeholderOther,
  disabled,
}: CommonProps<T>) => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext();

  const key = titleKey + '.edms';

  useEffect(() => {
    setValue(key, edms);
    trigger([key]);
  }, [edms]);

  const registerKey = register(key, edmsReferenceValidation(t));

  return (
    <div className={styles.activityTextContainer}>
      <Card className={styles.cardText}>
        <div className={styles.basicDiv}>
          <div className={styles.riskTextHeader}>{t(titleKey)}</div>
          <div className={styles.flexContainer}>
            {items?.map((item, index) => (
              <div className={styles.checkboxOption} key={index}>
                <RiskCheckbox
                  key={index}
                  label={t(`${checkboxKeyPrefix}.${item}`)}
                  checked={state ? (state as any)[item] : false}
                  onClick={() => {
                    if (setState) {
                      setState({ ...state, [item]: !(state as any)[item] });
                    }
                  }}
                  disabled={disabled}
                />
              </div>
            ))}
          </div>
          {state && otherKey && state[otherKey] && (
            <div className={styles.checkboxContainerText}>
              <GridColumnInputWrapper
                value={text}
                rows={1}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (setText) {
                    setText(e.target.value);
                  }
                }}
                disabled={disabled}
                render={(props: any) => <InputWrapper {...props} placeholder={placeholderOther} />}
              />
            </div>
          )}
          {(setEdms || edms) && (
            <div className={styles.checkboxContainerText}>
              <div className={styles.errorMessage}>
                {lodash.get(errors, key)?.message?.toString()}
              </div>
              <GridColumnInputWrapper
                value={edms}
                rows={1}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (setEdms) {
                    setEdms(e.target.value);
                  }
                }}
                disabled={disabled}
                render={(props: Record<string, unknown>) => (
                  <ExternalDocumentTagsUrlInputWrapper
                    {...registerKey}
                    {...props}
                    onlyUnique={true}
                    onlyAutocomplete={false}
                    value={edms}
                    setFieldValue={(field: string, value: string) => {
                      setEdms?.(value);
                    }}
                    placeholder={placeholder}
                    maxTags={1}
                  />
                )}
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default RiskCheckboxCommon;
